import { useState, useEffect, useCallback, lazy, Suspense } from 'react';
import { AppLayout } from '@/components/layout/AppLayout';
import { GlobalFilterProvider } from '@/contexts/GlobalFilterContext';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { LoginGate } from '@/components/LoginGate';
import { BrandLogo } from '@/components/BrandLogo';
import { DriverLocationReporter } from '@/components/DriverLocationReporter'; // #789 司机角色后台 GPS 上报
import './App.css';
import { NAV_ITEMS } from '@/data/mockData';
import { purgeExpiredTrash } from '@/lib/trash';
import { canAccessPage, usePermissionRevision } from '@/lib/permissions';

const BasicDataPage = lazy(() => import('@/pages/BasicDataPage').then((module) => ({ default: module.BasicDataPage })));
const Dashboard = lazy(() => import('@/pages/Dashboard').then((module) => ({ default: module.Dashboard })));
const PurchasePage = lazy(() => import('@/pages/PurchasePage').then((module) => ({ default: module.PurchasePage })));
const InventoryPage = lazy(() => import('@/pages/InventoryPage').then((module) => ({ default: module.InventoryPage })));
const SalesPage = lazy(() => import('@/pages/SalesPage').then((module) => ({ default: module.SalesPage })));
const FinancePage = lazy(() => import('@/pages/FinancePage').then((module) => ({ default: module.FinancePage })));
const MapPage = lazy(() => import('@/pages/MapPage').then((module) => ({ default: module.MapPage })));
const BarcodePage = lazy(() => import('@/pages/BarcodePage').then((module) => ({ default: module.BarcodePage })));
const AnalysisPage = lazy(() => import('@/pages/AnalysisPage').then((module) => ({ default: module.AnalysisPage })));
const SystemPage = lazy(() => import('@/pages/SystemPage').then((module) => ({ default: module.SystemPage })));
const AIAssistant = lazy(() => import('@/pages/AIAssistant').then((module) => ({ default: module.AIAssistant })));
const RetailPOS = lazy(() => import('@/pages/RetailPOS').then((module) => ({ default: module.RetailPOS })));
const QuotationPage = lazy(() => import('@/pages/QuotationPage').then((module) => ({ default: module.QuotationPage })));
const TodoPage = lazy(() => import('@/pages/TodoPage').then((module) => ({ default: module.TodoPage })));
const FestivalPromotionPage = lazy(() => import('@/pages/FestivalPromotionPage').then((module) => ({ default: module.FestivalPromotionPage })));

interface Tab {
  id: string;
  page: string;
  childId?: string;
  title: string;
}

const HOME_PAGE = 'dashboard';

function getPageTitle(page: string, childId?: string): string {
  // 尝试在 NAV_ITEMS 的 children 中找
  for (const item of NAV_ITEMS) {
    if (item.path === page) return item.label;
    if (item.children) {
      const child = item.children.find((c) => c.path === page || c.id === childId);
      if (child) return child.label;
    }
  }
  const titles: Record<string, string> = {
    'dashboard': '工作台',
    'basic-data': '基础数据',
    'purchase': '进货管理',
    'inventory': '库存管理',
    'sales': '销售管理',
    'finance': '财务管理',
    'map': '地图配送',
    'barcode': '条码打印',
    'analysis': '数据分析',
    'ai-assistant': 'AI 助手',
    'system': '系统管理',
    'retail': '零售收银',
    'quotation': '智能报价单',
    'festival-promotion': '节日促销',
  };
  return titles[page] || '页面';
}

function getInitialTab(page: string, childId?: string): string | undefined {
  if (page === 'basic-data') {
    const map: Record<string, string> = {
      'basic-data-customers': 'customers',
      'basic-data-suppliers': 'suppliers',
      'basic-data-warehouses': 'warehouses',
      'basic-data-products': 'products',
      'basic-data-festnotes': 'festnotes',
    };
    return childId ? map[childId] : 'customers';
  }
  if (page === 'purchase') {
    const map: Record<string, string> = {
      'purchase-po': 'po',
      // 'purchase-waiting' 已合并在途采购 tab，保留映射以兼容旧的多标签页/收藏入口
      'purchase-waiting': 'po',
      'purchase-checking': 'checking',
      'purchase-inbound': 'inbound',
      'purchase-payable': 'payable',
      'purchase-return': 'return',
      'purchase-detail': 'detail',
      'purchase-history': 'history',
      'purchase-analysis': 'analysis',
    };
    return childId ? map[childId] : 'po';
  }
  if (page === 'inventory') {
    const map: Record<string, string> = {
      'inventory-stock': 'stock',
      'inventory-batch': 'batch',
      'inventory-outbound': 'outbound',
      'inventory-warning': 'warning',
    };
    return childId ? map[childId] : 'stock';
  }
  if (page === 'sales') {
    const map: Record<string, string> = {
      'sales-pending': 'pending',
      'sales-delivering': 'delivering',
      'sales-receivables': 'receivables',
      'sales-order': 'orders',
      'sales-product-details': 'product-details',
      'sales-return': 'returns',
    };
    return childId ? map[childId] : 'pending';
  }
  if (page === 'finance') {
    const map: Record<string, string> = {
      'finance-receivable': 'receivable',
      'finance-payable': 'payable',
      'finance-reconcile': 'reconcile',
    };
    return childId ? map[childId] : 'receivable';
  }
  if (page === 'map') {
    const map: Record<string, string> = {
      'map-customers': 'customers',
      'map-addresses': 'addresses',
      'map-drivers': 'drivers',
      'map-route': 'route',
    };
    return childId ? map[childId] : 'customers';
  }
  if (page === 'barcode') {
    const map: Record<string, string> = {
      'barcode-generate': 'generate',
      'barcode-label': 'label',
      'barcode-delivery': 'delivery',
      'barcode-grid': 'grid',
      'barcode-store': 'store',
      'barcode-cloud': 'cloud',
    };
    return childId ? map[childId] : 'generate';
  }
  if (page === 'analysis') {
    const map: Record<string, string> = {
      'analysis-profit': 'profit',
      'analysis-product': 'product',
      'analysis-customer': 'customer',
    };
    return childId ? map[childId] : 'profit';
  }
  if (page === 'system') {
    const map: Record<string, string> = {
      'system-users': 'users',
      'system-role': 'role',
      'system-log': 'log',
      'system-data': 'data',
      'system-security': 'security',
      'system-backup': 'backup',
      'system-sync': 'sync',
      'system-api': 'api',
      'system-brand': 'brand',
    };
    return childId ? map[childId] : 'users';
  }
  return undefined;
}

/** 根组件：AuthProvider 包裹（登录态校验），内部再走登录门 */
function App() {
  return (
    <AuthProvider>
      <AppInner />
    </AuthProvider>
  );
}

function AppInner() {
  // 登录门：未登录显示 LoginGate（AuthProvider 内完成 token 校验）
  const { status } = useAuth();
  if (status === 'checking') {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', flexDirection: 'column', gap: 12, background: '#fafafa' }}>
        <BrandLogo size={36} rounded="rounded-[10px]" />
        <div style={{ color: '#71717a', fontSize: 13 }}>正在验证登录状态…</div>
      </div>
    );
  }
  if (status === 'login') return <LoginGate />;
  return <MainApp />;
}

function MainApp() {
  const { user } = useAuth();
  const permissionRevision = usePermissionRevision();
  // 保留权限变更订阅以触发权限重算；不再把 revision 用作页面 key，避免卸载正在填写的表单。
  void permissionRevision;
  const [tabs, setTabs] = useState<Tab[]>([{ id: 'home', page: HOME_PAGE, title: '工作台' }]);
  const [activeTabId, setActiveTabId] = useState<string>('home');
  const [activeChildId, setActiveChildId] = useState<string | undefined>(undefined);

  const activeTab = tabs.find((t) => t.id === activeTabId) || tabs[0];

  const handleNavigate = useCallback((page: string, childId?: string) => {
    if (!canAccessPage(user?.role, page)) return;
    // 同一页面且同一 childId：直接激活
    const existing = tabs.find((t) => t.page === page && t.childId === childId);
    if (existing) {
      setActiveTabId(existing.id);
      setActiveChildId(childId);
      return;
    }

    // 同一页面不同 childId：更新当前 tab 的 childId
    const samePageTab = tabs.find((t) => t.page === page);
    if (samePageTab && !childId) {
      setActiveTabId(samePageTab.id);
      return;
    }

    const newTab: Tab = {
      id: `${page}-${Date.now()}`,
      page,
      childId,
      title: getPageTitle(page, childId),
    };
    setTabs((prev) => [...prev, newTab]);
    setActiveTabId(newTab.id);
    setActiveChildId(childId);
  }, [tabs, user?.role]);

  const handleCloseTab = useCallback((tabId: string) => {
    setTabs((prev) => {
      const idx = prev.findIndex((t) => t.id === tabId);
      if (idx < 0) return prev;
      const next = prev.filter((t) => t.id !== tabId);
      if (activeTabId === tabId) {
        const fallback = prev[idx - 1] || next[0];
        if (fallback) {
          setActiveTabId(fallback.id);
          setActiveChildId(fallback.childId);
        }
      }
      // 不允许关闭最后一个非首页 tab 后空着：如果没有 tab 了，回到首页
      if (next.length === 0) {
        const home: Tab = { id: 'home', page: HOME_PAGE, title: '工作台' };
        setActiveTabId('home');
        setActiveChildId(undefined);
        return [home];
      }
      return next;
    });
  }, [activeTabId]);

  const handleTabChange = useCallback((tabId: string) => {
    const tab = tabs.find((t) => t.id === tabId);
    if (tab) {
      setActiveTabId(tabId);
      setActiveChildId(tab.childId);
    }
  }, [tabs]);

  // Set page title
  useEffect(() => {
    document.title = activeTab ? `${activeTab.title} - 昌发工作台` : '昌发工作台';
  }, [activeTab]);

  // 应用启动时自动清理回收站中已过期（>7 天）的记录
  useEffect(() => {
    purgeExpiredTrash().catch(() => {
      // IndexedDB 不可用时静默跳过，不影响应用启动
    });
  }, []);

  const renderPage = (page: string, childId?: string) => {
    if (!canAccessPage(user?.role, page)) {
      return (
        <div className="h-full flex flex-col items-center justify-center gap-3 bg-zinc-50 text-center px-6">
          <div className="text-lg font-semibold text-zinc-800">无权访问此页面</div>
          <p className="text-sm text-zinc-500">请联系老板在“角色权限”中开通相应权限。</p>
          <button onClick={() => handleNavigate(HOME_PAGE)} className="px-4 py-2 rounded-md bg-emerald-600 text-white text-sm">返回工作台</button>
        </div>
      );
    }
    const initialTab = getInitialTab(page, childId);

    switch (page) {
      case 'dashboard': return <Dashboard onNavigate={handleNavigate} />;
      case 'todo': return <TodoPage />;
      case 'basic-data': return <BasicDataPage key={initialTab} initialTab={initialTab} />;
      case 'purchase': return <PurchasePage key={initialTab} initialTab={initialTab} />;
      case 'inventory': return <InventoryPage initialTab={initialTab} />;
      case 'sales': return <SalesPage key={initialTab} onNavigate={handleNavigate} initialTab={initialTab} />;
      case 'finance': return <FinancePage key={initialTab} initialTab={initialTab} />;
      case 'map': return <MapPage initialTab={initialTab} />;
      case 'barcode': return <BarcodePage initialTab={initialTab} />;
      case 'analysis': return <AnalysisPage key={initialTab} initialTab={initialTab} />;
      case 'ai-assistant': return <AIAssistant />;
      case 'system': return <SystemPage initialTab={initialTab} onNavigate={handleNavigate} />;
      case 'retail': return <RetailPOS />;
      case 'quotation': return <QuotationPage onNavigate={handleNavigate} />;
      case 'festival-promotion': return <FestivalPromotionPage />;
      default: return <Dashboard onNavigate={handleNavigate} />;
    }
  };

  return (
    <GlobalFilterProvider>
      {/* #789 司机角色登录后全局后台上报位置（不渲染 UI） */}
      <DriverLocationReporter />
      <AppLayout
        currentPage={activeTab.page}
        onNavigate={handleNavigate}
        activeChildId={activeChildId}
        tabs={tabs}
        activeTabId={activeTabId}
        onTabChange={handleTabChange}
        onCloseTab={handleCloseTab}
      >
        <Suspense fallback={<div className="flex h-full items-center justify-center text-sm text-zinc-400">页面加载中...</div>}>
          <div className="h-full">{renderPage(activeTab.page, activeTab.childId)}</div>
        </Suspense>
      </AppLayout>
    </GlobalFilterProvider>
  );
}

export default App;
